import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { type GridSelectionModel } from "@mui/x-data-grid-pro";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { type AcquiredProperty, type AcquiredPropertySearch } from "../types";
import {
  DOCUMENT_COLUMNS,
  REAL_ESTATE_LABELS,
  canReacquire,
  getCompanyNames,
  getPropertyExportRows,
} from "../utils/propertyPresentation";
import { MobileList, CardRow } from "./MobileList";
import { DocumentCell } from "./DocumentCell";
import { PropertyExportButtons } from "./PropertyExportToolbar";

interface Props {
  rows: AcquiredProperty[];
  search: AcquiredPropertySearch;
  count: number;
  loading: boolean;
  hasError: boolean;
  onSearchChange: (next: AcquiredPropertySearch) => void;
  selectedIds: GridSelectionModel;
  onSelect: (ids: GridSelectionModel) => void;
  isReadOnly: boolean;
  onReacquire: (row: AcquiredProperty) => void;
  userId: string;
  isSharable: boolean;
}

export const MobilePropertyCards: React.FC<Props> = ({
  rows,
  search,
  count,
  loading,
  hasError,
  onSearchChange,
  selectedIds,
  onSelect,
  isReadOnly,
  onReacquire,
  userId,
  isSharable,
}) => {
  const location = useLocation();
  const hasCompany = rows.some((row) => row.isDatalensRequest);
  return (
    <>
      <Box sx={{ mb: 2 }}>
        <PropertyExportButtons
          selectedCount={selectedIds.length}
          pageRowCount={rows.length}
          getRows={() =>
            getPropertyExportRows(
              rows.filter((row) => selectedIds.includes(row.id)),
              hasCompany
            )
          }
        />
      </Box>
      <MobileList
        search={search}
        count={count}
        loading={loading}
        empty={!rows.length}
        emptyMessage={
          hasError
            ? "一覧を取得できませんでした"
            : "条件に一致する物件がありません"
        }
        onChange={onSearchChange}
      >
        {rows.map((row) => (
          <Card key={row.id} variant="outlined">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
                <Checkbox
                  checked={selectedIds.includes(row.id)}
                  inputProps={{
                    "aria-label": `物件 ${row.id} を出力対象に選択`,
                  }}
                  onChange={(_event, checked) => {
                    onSelect(
                      checked
                        ? [...selectedIds, row.id]
                        : selectedIds.filter((id) => id !== row.id)
                    );
                  }}
                />
                <Link
                  component={RouterLink}
                  to={`/acquirebookstatus/${row.pictureRequestId}/`}
                  state={{
                    acquiredListReturnTo: location.pathname + location.search,
                    acquiredListUserId: userId,
                  }}
                  aria-label={`依頼No. ${row.pictureRequestId} の詳細`}
                  sx={{
                    fontWeight: 700,
                    overflowWrap: "anywhere",
                    minWidth: 0,
                  }}
                >
                  {row.identificationName ?? `依頼No. ${row.pictureRequestId}`}
                </Link>
              </Stack>
              <CardRow label="取得依頼日">
                <Typography variant="body2">
                  {format(parseISO(row.requestedAt), "yyyy/MM/dd HH:mm")}
                </Typography>
              </CardRow>
              {hasCompany && (
                <CardRow label="会社名">
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {getCompanyNames(row)}
                  </Typography>
                </CardRow>
              )}
              <CardRow label="種別">
                <Typography variant="body2">
                  {REAL_ESTATE_LABELS[row.realEstateType] ?? row.realEstateType}
                </Typography>
              </CardRow>
              <CardRow label="都道府県">
                <Typography variant="body2">{row.prefectureName}</Typography>
              </CardRow>
              <CardRow label="所在">
                <Typography variant="body2">{row.location}</Typography>
              </CardRow>
              <CardRow label="地番または家屋番号">
                <Typography variant="body2">{row.chibanKaokuNumber}</Typography>
              </CardRow>
              {isSharable && row.isOtherUserResource && (
                <CardRow label="取得ユーザー">
                  <Typography variant="body2">他ユーザー取得</Typography>
                </CardRow>
              )}
              {DOCUMENT_COLUMNS.map(([type, title]) => (
                <CardRow key={type} label={title}>
                  <DocumentCell
                    documents={row.documents.filter(
                      (document) => document.pictureType === type
                    )}
                    filters={search}
                  />
                </CardRow>
              ))}
              <Stack alignItems="flex-end" sx={{ mt: 1 }}>
                <ReadOnlyTooltip isReadOnly={isReadOnly}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isReadOnly || !canReacquire(row)}
                      onClick={() => {
                        onReacquire(row);
                      }}
                    >
                      再取得に進む
                    </Button>
                  </span>
                </ReadOnlyTooltip>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </MobileList>
    </>
  );
};
